import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FiArrowLeft, FiEdit3, FiUser, FiBriefcase, FiDollarSign, FiPackage, FiChevronLeft, FiChevronRight, FiTrash2 } from 'react-icons/fi';
import BottomTabMenu from '../../components/UI/BottomTabMenu';
import ConfirmModal from '../../components/UI/Modal/ConfirmModal';
import { vendedoresService, productosService } from '../../services';

const VendedorDetalle = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [vendedor, setVendedor] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Estados para el carousel de productos
  const [productos, setProductos] = useState([]);
  const [currentProductIndex, setCurrentProductIndex] = useState(0);
  const [isLoadingProducts, setIsLoadingProducts] = useState(false);

  // Opciones para los selectores
  const frecuenciasPago = [
    { value: 'WEEKLY', label: 'SEMANAL' },
    { value: 'BIWEEKLY', label: 'QUINCENAL' },
    { value: 'MONTHLY', label: 'MENSUAL' }
  ];

  const metodosPago = [
    { value: 'CHECK', label: 'CHEQUE' },
    { value: 'BANK_TRANSFER', label: 'DEPÓSITO' },
    { value: 'ZELLE', label: 'ZELLE' }
  ];

  const loadVendedorDetalle = useCallback(async () => {
    if (!id) return;

    setIsLoading(true);
    setError(null);

    try {
      const result = await vendedoresService.getVendedor(id);

      if (result.success) {
        // Transformar datos de la API al formato esperado por el componente
        const apiData = result.data;

        // Parsear la dirección si existe
        let addressData = {
          calle: '',
          unidad: '',
          ciudad: '',
          estado: '',
          codigoPostal: '',
          pais: ''
        };

        if (apiData.address) {
          try {
            const parsed = JSON.parse(apiData.address);
            addressData = {
              calle: parsed.street || '',
              unidad: parsed.unit || '',
              ciudad: parsed.city || '',
              estado: parsed.state || '',
              codigoPostal: parsed.postal_code || '',
              pais: parsed.country || ''
            };
          } catch (e) {
            // Si no es JSON, usar como texto plano
            addressData.calle = apiData.address;
          }
        }

        // Normalizar teléfonos: la API trae `phones` (array) y/o `phone` (legacy).
        // Si no hay `phones`, derivar uno desde el legacy para mantener compatibilidad.
        let phonesArr = [];
        if (Array.isArray(apiData.phones) && apiData.phones.length > 0) {
          phonesArr = apiData.phones;
        } else if (apiData.phone) {
          phonesArr = [{ number: apiData.phone, type: 'mobile', is_primary: true }];
        }

        const transformedData = {
          nombre: apiData.full_name || '',
          email: apiData.email || '',
          telefono: apiData.phone || '',
          phones: phonesArr,
          ...addressData,
          nombreBanco: apiData.bank_name || '',
          numeroCuenta: apiData.bank_account || '',
          comisionPorcentaje: apiData.commission_percentage ? parseFloat(apiData.commission_percentage) : 0,
          frecuenciaPago: apiData.payment_period || 'MONTHLY',
          metodoPago: apiData.payment_method || 'BANK_TRANSFER'
        };
        setVendedor(transformedData);
      } else {
        setError(result.message || 'Error al cargar el vendedor');
      }
    } catch (err) {
      console.error('Error al cargar vendedor:', err);
      setError('Error al cargar el vendedor');
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadVendedorDetalle();
  }, [loadVendedorDetalle]);

  // Cargar los últimos 4 productos para el carousel
  useEffect(() => {
    loadProductos();
  }, []);

  const loadProductos = async () => {
    setIsLoadingProducts(true);
    try {
      // SOLO productos marcados explícitamente como "nuevos" (checkbox en el
      // producto). Se priorizan los que tienen foto para el carrusel.
      const response = await productosService.getProductos({
        status: 'ACTIVE',
        is_new: true,
        limit: 12
      });

      if (response.success && response.data) {
        const lista = response.data;
        const conFoto = lista.filter(p => Array.isArray(p.images) && p.images.length > 0);
        const sinFoto = lista.filter(p => !Array.isArray(p.images) || p.images.length === 0);
        setProductos([...conFoto, ...sinFoto]);
      }
    } catch (error) {
      console.error('Error al cargar productos:', error);
    } finally {
      setIsLoadingProducts(false);
    }
  };

  const handlePrevProduct = () => {
    setCurrentProductIndex((prev) =>
      prev === 0 ? productos.length - 1 : prev - 1
    );
  };

  const handleNextProduct = () => {
    setCurrentProductIndex((prev) =>
      prev === productos.length - 1 ? 0 : prev + 1
    );
  };

  const getLabelFromOptions = (options, value) => {
    const option = options.find(opt => opt.value === value);
    return option ? option.label : value || 'N/A';
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      const result = await vendedoresService.deleteVendedor(id);
      if (result.success) {
        navigate('/system/vendedores', {
          state: {
            message: `Vendedor "${vendedor?.nombre || ''}" desactivado correctamente`,
            type: 'success'
          }
        });
      } else {
        console.error('Error al desactivar vendedor:', result.message);
      }
    } catch (error) {
      console.error('Error desactivando vendedor:', error);
    } finally {
      setIsDeleting(false);
      setShowDeleteModal(false);
    }
  };

  if (isLoading) {
    return (
      <div className="page-loading">
        <div className="loading-spinner-single" style={{ width: '40px', height: '40px' }}></div>
        <div className="loading-message">Cargando vendedor...</div>
      </div>
    );
  }

  if (error || !vendedor) {
    return (
      <div className="cliente-form-page">
        <div className="form-header">
          <button
            className="btn btn--ghost"
            onClick={() => navigate('/system/vendedores')}
            type="button"
          >
            <FiArrowLeft /> Volver a Vendedores
          </button>
        </div>
        <div className="alert alert--danger">
          {error || 'Vendedor no encontrado'}
        </div>
      </div>
    );
  }

  return (
    <div className="cliente-form-page">
      {/* Header */}
      <div className="form-header">
        <button
          className="btn btn--ghost"
          onClick={() => navigate('/system/vendedores')}
          type="button"
        >
          <FiArrowLeft /> Volver a Vendedores
        </button>

        <h1 className="form-title">
          DETALLE DE VENDEDOR
        </h1>

        <div className="form-actions">
          <button
            className="btn btn--outline"
            onClick={() => setShowDeleteModal(true)}
            type="button"
            disabled={isDeleting}
          >
            <FiTrash2 /> Eliminar
          </button>
          <button
            className="btn btn--primary"
            onClick={() => navigate(`/system/vendedores/${id}/editar`)}
            type="button"
          >
            <FiEdit3 /> Editar Vendedor
          </button>
        </div>
      </div>

      {/* Formulario (campos deshabilitados) */}
      <div className="cliente-form">
        <div className="form-columns">
          {/* Columna Izquierda */}
          <div className="form-column">
            {/* Sección: Datos Personales */}
            <div className="form-section">
              <div className="section-header">
                <FiUser className="section-icon" />
                <h2 className="section-title">Datos Personales</h2>
              </div>

              <div className="form-group">
                <label className="form-label">Nombre</label>
                <input
                  type="text"
                  className="form-input"
                  value={vendedor.nombre || ''}
                  disabled
                  readOnly
                />
              </div>

              <div className="form-group">
                <label className="form-label">
                  {vendedor.phones && vendedor.phones.length > 1 ? 'Teléfonos' : 'Teléfono'}
                </label>
                {vendedor.phones && vendedor.phones.length > 0 ? (
                  (() => {
                    const phoneTypeLabels = {
                      mobile: 'Móvil', office: 'Oficina', home: 'Casa', fax: 'Fax', other: 'Otro'
                    };
                    return vendedor.phones.map((p, i) => (
                      <div
                        key={i}
                        style={{ display: 'flex', gap: '8px', marginBottom: '8px', alignItems: 'center' }}
                      >
                        <input
                          type="text"
                          className="form-input"
                          value={phoneTypeLabels[p.type] || p.type || 'Móvil'}
                          disabled
                          readOnly
                          style={{ width: '140px', height: '42px', padding: '10px 12px' }}
                        />
                        <input
                          type="tel"
                          className="form-input"
                          value={p.number || ''}
                          disabled
                          readOnly
                          style={{ flex: 1 }}
                        />
                        {p.is_primary && (
                          <span
                            title="Teléfono principal"
                            style={{
                              padding: '4px 10px',
                              background: '#7B2D8E',
                              color: 'white',
                              borderRadius: '12px',
                              fontSize: '11px',
                              fontWeight: '700',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            PRINCIPAL
                          </span>
                        )}
                      </div>
                    ));
                  })()
                ) : (
                  <input
                    type="tel"
                    className="form-input"
                    value=""
                    disabled
                    readOnly
                    placeholder="Sin teléfono registrado"
                  />
                )}
              </div>

              <div className="form-group">
                <label className="form-label">Email</label>
                <input
                  type="email"
                  className="form-input"
                  value={vendedor.email || ''}
                  disabled
                  readOnly
                />
              </div>
            </div>

            {/* Sección: Dirección */}
            <div className="form-section">
              <div className="section-header">
                <h3 className="section-title">Dirección</h3>
              </div>

              <div className="form-group">
                <label className="form-label">Número y Calle</label>
                <input
                  type="text"
                  className="form-input"
                  value={vendedor.calle || ''}
                  disabled
                  readOnly
                />
              </div>

              <div className="form-group">
                <label className="form-label">Unidad</label>
                <input
                  type="text"
                  className="form-input"
                  value={vendedor.unidad || ''}
                  disabled
                  readOnly
                />
              </div>

              <div className="form-grid-2">
                <div className="form-group">
                  <label className="form-label">Ciudad</label>
                  <input
                    type="text"
                    className="form-input"
                    value={vendedor.ciudad || ''}
                    disabled
                    readOnly
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Estado</label>
                  <input
                    type="text"
                    className="form-input"
                    value={vendedor.estado || ''}
                    disabled
                    readOnly
                  />
                </div>
              </div>

              <div className="form-grid-2">
                <div className="form-group">
                  <label className="form-label">Código Postal</label>
                  <input
                    type="text"
                    className="form-input"
                    value={vendedor.codigoPostal || ''}
                    disabled
                    readOnly
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">País</label>
                  <input
                    type="text"
                    className="form-input"
                    value={vendedor.pais || ''}
                    disabled
                    readOnly
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Columna Derecha */}
          <div className="form-column">
            {/* Sección: Información Bancaria */}
            <div className="form-section">
              <div className="section-header">
                <h3 className="section-title">Información de Cuenta Bancaria</h3>
              </div>

              <div className="form-group">
                <label className="form-label">Número de Cuenta</label>
                <input
                  type="text"
                  className="form-input"
                  value={vendedor.numeroCuenta || ''}
                  disabled
                  readOnly
                />
              </div>

              <div className="form-group">
                <label className="form-label">Banco Receptor</label>
                <input
                  type="text"
                  className="form-input"
                  value={vendedor.nombreBanco || ''}
                  disabled
                  readOnly
                />
              </div>
            </div>

            {/* Sección: Comisiones y Pagos */}
            <div className="form-section">
              <div className="section-header">
                <FiDollarSign className="section-icon" />
                <h3 className="section-title">Comisiones y Pagos</h3>
              </div>

              <div className="form-group">
                <label className="form-label">% de Comisión</label>
                <input
                  type="number"
                  className="form-input"
                  value={vendedor.comisionPorcentaje || '0'}
                  disabled
                  readOnly
                />
              </div>

              <div className="form-group">
                <label className="form-label">Frecuencia de Pago</label>
                <input
                  type="text"
                  className="form-input"
                  value={getLabelFromOptions(frecuenciasPago, vendedor.frecuenciaPago)}
                  disabled
                  readOnly
                />
              </div>

              <div className="form-group">
                <label className="form-label">Forma de Pago</label>
                <input
                  type="text"
                  className="form-input"
                  value={getLabelFromOptions(metodosPago, vendedor.metodoPago)}
                  disabled
                  readOnly
                />
              </div>
            </div>

            {/* Sección: Carousel de Productos */}
            <div className="form-section">
              <div className="section-header">
                <FiPackage className="section-icon" />
                <h3 className="section-title">Productos Nuevos</h3>
              </div>

              {isLoadingProducts ? (
                <div style={{ display: 'flex', justifyContent: 'center', padding: '20px' }}>
                  <div className="loading-spinner-single" style={{ width: '30px', height: '30px' }}></div>
                </div>
              ) : productos.length > 0 ? (
                <div style={{
                  position: 'relative',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px'
                }}>
                  {/* Flecha Izquierda */}
                  <button
                    type="button"
                    onClick={handlePrevProduct}
                    style={{
                      background: '#4a3470',
                      color: 'white',
                      border: 'none',
                      borderRadius: '50%',
                      width: '40px',
                      height: '40px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      flexShrink: 0,
                      transition: 'all 0.2s',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                    }}
                    onMouseOver={(e) => {
                      e.currentTarget.style.background = '#3d2b5c';
                      e.currentTarget.style.transform = 'scale(1.1)';
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.background = '#4a3470';
                      e.currentTarget.style.transform = 'scale(1)';
                    }}
                  >
                    <FiChevronLeft size={22} />
                  </button>

                  {/* Contenedor de 3 productos */}
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(4, 1fr)',
                    gap: '12px',
                    flex: 1,
                    maxWidth: '100%'
                  }}>
                    {[0, 1, 2, 3].map((offset) => {
                      const index = (currentProductIndex + offset) % productos.length;
                      const producto = productos[index];

                      // Obtener la imagen principal o la primera imagen disponible
                      const getProductImage = (product) => {
                        if (!product.images || product.images.length === 0) return null;

                        // Buscar imagen principal
                        const primaryImage = product.images.find(img => img.is_primary);
                        if (primaryImage) return primaryImage;

                        // Si no hay principal, usar la primera
                        return product.images[0];
                      };

                      const imagen = getProductImage(producto);
                      const API_BASE_URL = process.env.REACT_APP_API_URL?.replace('/api/v1', '') || 'http://localhost:8000';
                      const imageUrl = imagen ? `${API_BASE_URL}${imagen.medium_url}` : null;

                      return (
                        <div
                          key={index}
                          onClick={() => navigate(`/system/productos/${producto.id}`)}
                          style={{
                            width: '100%',
                            paddingBottom: '100%',
                            position: 'relative',
                            background: '#f8f9fa',
                            borderRadius: '10px',
                            overflow: 'hidden',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                            transition: 'transform 0.2s, box-shadow 0.2s, border-color 0.2s',
                            cursor: 'pointer',
                            border: '2px solid #e9ecef'
                          }}
                          onMouseOver={(e) => {
                            e.currentTarget.style.transform = 'scale(1.05)';
                            e.currentTarget.style.boxShadow = '0 4px 16px rgba(74, 52, 112, 0.2)';
                            e.currentTarget.style.borderColor = '#4a3470';
                          }}
                          onMouseOut={(e) => {
                            e.currentTarget.style.transform = 'scale(1)';
                            e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)';
                            e.currentTarget.style.borderColor = '#e9ecef';
                          }}
                        >
                          {imageUrl ? (
                            <img
                              src={imageUrl}
                              alt={imagen.alt_text || 'Producto'}
                              style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover'
                              }}
                              onError={(e) => {
                                e.target.style.display = 'none';
                                const parent = e.target.parentElement;
                                const iconDiv = document.createElement('div');
                                iconDiv.style.cssText = 'position: absolute; top: 0; left: 0; width: 100%; height: 100%; display: flex; align-items: center; justify-content: center;';
                                iconDiv.innerHTML = '<svg stroke="currentColor" fill="none" stroke-width="2" viewBox="0 0 24 24" stroke-linecap="round" stroke-linejoin="round" style="width: 50%; height: 50%; color: #6c757d;" xmlns="http://www.w3.org/2000/svg"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect><circle cx="8.5" cy="8.5" r="1.5"></circle><polyline points="21 15 16 10 5 21"></polyline></svg>';
                                parent.appendChild(iconDiv);
                              }}
                            />
                          ) : (
                            <div style={{
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              width: '100%',
                              height: '100%',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}>
                              <FiPackage style={{ fontSize: '64px', color: '#6c757d' }} />
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  {/* Flecha Derecha */}
                  <button
                    type="button"
                    onClick={handleNextProduct}
                    style={{
                      background: '#4a3470',
                      color: 'white',
                      border: 'none',
                      borderRadius: '50%',
                      width: '40px',
                      height: '40px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      flexShrink: 0,
                      transition: 'all 0.2s',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                    }}
                    onMouseOver={(e) => {
                      e.currentTarget.style.background = '#3d2b5c';
                      e.currentTarget.style.transform = 'scale(1.1)';
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.background = '#4a3470';
                      e.currentTarget.style.transform = 'scale(1)';
                    }}
                  >
                    <FiChevronRight size={22} />
                  </button>
                </div>
              ) : (
                <div style={{
                  textAlign: 'center',
                  padding: '20px',
                  color: '#6c757d',
                  fontSize: '14px'
                }}>
                  No hay productos disponibles
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Menú de navegación inferior */}
      <BottomTabMenu
        baseRoute={`/system/vendedores/${id}`}
        currentTab="datos"
        tabs={[
          {
            id: 'datos',
            label: 'Datos',
            route: ''
          },
          {
            id: 'ventas',
            label: 'Reporte de Ventas',
            route: '/ventas'
          },
          {
            id: 'comisiones',
            label: 'Reporte de Comisiones',
            route: '/comisiones'
          },
          {
            id: 'clientes',
            label: 'Lista de Clientes',
            route: '/clientes'
          },
          {
            id: 'grafica',
            label: 'Gráfica de Ventas',
            route: '/grafica'
          },
          {
            id: 'notas',
            label: 'Notas',
            route: '/notas'
          }
        ]}
      />

      {/* Modal de confirmación de borrado lógico */}
      <ConfirmModal
        isOpen={showDeleteModal}
        onCancel={() => setShowDeleteModal(false)}
        onConfirm={handleDelete}
        title="Desactivar Vendedor"
        message={`¿Desactivar el vendedor "${vendedor?.nombre || ''}"? Su cuenta quedará inactiva pero sus datos se conservan.`}
        confirmText="Desactivar"
        cancelText="Cancelar"
        isLoading={isDeleting}
      />
    </div>
  );
};

export default VendedorDetalle;
